import React, { useEffect, useState, useCallback } from 'react';
import { sb } from '../../services/supabase';
import { PageHeader, Card, CardHeader, Table, Td, EmptyRow, Btn, Badge, Modal, FormField, Input, StatCard, useToast, FeatureLocked } from '../../components/ui';

export function Payroll({ companyId, featureEnabled }: { companyId: string; featureEnabled: boolean }) {
  const toast = useToast();
  const [payrollRuns, setPayrollRuns] = useState<any[]>([]);
  const [employees, setEmployees] = useState<any[]>([]);
  const [selectedRun, setSelectedRun] = useState<any>(null);
  const [payrollRecords, setPayrollRecords] = useState<any[]>([]);
  const [processing, setProcessing] = useState(false);
  const [salaryModal, setSalaryModal] = useState<any>(null);
  const [salaryForm, setSalaryForm] = useState({ basic_salary: '', hra: '', allowances: '' });
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());

  const load = useCallback(async () => {
    const [{ data: runs }, { data: emps }] = await Promise.all([
      sb.from('payroll_runs').select('*').eq('company_id', companyId).order('created_at', { ascending: false }),
      sb.from('users').select('*, salary_structures(*)').eq('company_id', companyId).in('role', ['employee', 'field_executive', 'manager']).eq('is_active', true),
    ]);
    setPayrollRuns(runs || []);
    setEmployees(emps || []);
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  if (!featureEnabled) return <FeatureLocked feature="Payroll" />;

  const loadRunRecords = async (runId: string) => {
    const { data } = await sb.from('payroll_records').select('*, user:users(full_name, employee_code)').eq('payroll_run_id', runId).order('created_at');
    setPayrollRecords(data || []);
  };

  const runPayroll = async () => {
    if (!window.confirm(`Run payroll for ${getMonthName(month)} ${year}? This will calculate salaries for all active employees.`)) return;
    setProcessing(true);
    try {
      // Create or get payroll run
      const { data: existing } = await sb.from('payroll_runs').select('*').eq('company_id', companyId).eq('month', month).eq('year', year).single();
      let runId = existing?.id;

      if (!runId) {
        const { data: newRun } = await sb.from('payroll_runs').insert({ company_id: companyId, month, year, status: 'processing' }).select().single();
        runId = newRun?.id;
      }

      if (!runId) throw new Error('Failed to create payroll run');

      // Get attendance for the month
      const fromDate = `${year}-${String(month).padStart(2,'0')}-01`;
      const toDate = `${year}-${String(month).padStart(2,'0')}-${new Date(year, month, 0).getDate()}`;

      const { data: attendance } = await sb.from('attendance').select('user_id, status, total_hours').in('user_id', employees.map(e => e.id)).gte('date', fromDate).lte('date', toDate);

      const workingDays = new Date(year, month, 0).getDate();
      let totalPayroll = 0;
      const records = [];

      for (const emp of employees) {
        const empAtt = (attendance || []).filter((a: any) => a.user_id === emp.id);
        const presentDays = empAtt.filter((a: any) => a.status === 'present').length;
        const lateDays = empAtt.filter((a: any) => a.status === 'late').length;
        const leaveDays = empAtt.filter((a: any) => a.status === 'on_leave').length;
        const absentDays = workingDays - presentDays - lateDays - leaveDays;

        const salary = emp.salary_structures?.[0];
        const basic = salary?.basic_salary || 0;
        const hra = salary?.hra || 0;
        const allowances = salary?.allowances || 0;
        const grossPerDay = (basic + hra + allowances) / workingDays;
        const grossSalary = grossPerDay * (presentDays + lateDays + leaveDays);
        const pfDeduction = grossSalary * ((salary?.pf_employee || 12) / 100);
        const deductions = pfDeduction;
        const netSalary = grossSalary - deductions;

        totalPayroll += netSalary;
        records.push({
          payroll_run_id: runId, user_id: emp.id, company_id: companyId,
          basic_salary: basic, working_days: workingDays,
          present_days: presentDays, absent_days: Math.max(0, absentDays),
          late_days: lateDays, leave_days: leaveDays,
          gross_salary: Math.round(grossSalary * 100) / 100,
          deductions: Math.round(deductions * 100) / 100,
          net_salary: Math.round(netSalary * 100) / 100,
          status: 'calculated',
        });
      }

      // Delete old records for this run and insert new ones
      await sb.from('payroll_records').delete().eq('payroll_run_id', runId);
      if (records.length > 0) await sb.from('payroll_records').insert(records);

      await sb.from('payroll_runs').update({ status: 'draft', total_amount: Math.round(totalPayroll * 100) / 100 }).eq('id', runId);

      toast(`✅ Payroll calculated for ${records.length} employees`, 'success');
      await load();
    } catch (e: any) {
      toast(e.message, 'error');
    } finally {
      setProcessing(false);
    }
  };

  const approveRun = async (runId: string) => {
    const { data: { user } } = await sb.auth.getUser();
    await sb.from('payroll_runs').update({ status: 'approved', processed_by: user?.id, processed_at: new Date().toISOString() }).eq('id', runId);
    toast('Payroll approved!', 'success'); load();
  };

  const saveSalaryStructure = async () => {
    if (!salaryModal) return;
    const existing = salaryModal.salary_structures?.[0];
    const data = { user_id: salaryModal.id, company_id: companyId, basic_salary: parseFloat(salaryForm.basic_salary) || 0, hra: parseFloat(salaryForm.hra) || 0, allowances: parseFloat(salaryForm.allowances) || 0 };
    if (existing) await sb.from('salary_structures').update(data).eq('id', existing.id);
    else await sb.from('salary_structures').insert(data);
    toast('Salary structure saved!', 'success'); setSalaryModal(null); load();
  };

  const openSalary = (emp: any) => {
    const s = emp.salary_structures?.[0];
    setSalaryForm({ basic_salary: s?.basic_salary || '', hra: s?.hra || '', allowances: s?.allowances || '' });
    setSalaryModal(emp);
  };

  const getMonthName = (m: number) => ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][m-1];

  const totalPayroll = payrollRuns.reduce((s, r) => s + (r.total_amount || 0), 0);
  const empWithSalary = employees.filter(e => e.salary_structures?.length > 0).length;

  return (
    <div className="animate-fadeUp" style={{ padding: 32 }}>
      <PageHeader title="Payroll" subtitle="Calculate and manage monthly payroll" />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16, marginBottom: 24 }}>
        <StatCard label="Active Employees" value={employees.length} delta="Eligible for payroll" color="var(--accent)" />
        <StatCard label="Salary Configured" value={empWithSalary} delta={`${employees.length - empWithSalary} missing salary`} color="#845EC2" />
        <StatCard label="Total Paid (All Time)" value={`₹${totalPayroll.toLocaleString('en-IN')}`} delta="Across all runs" color="var(--warn)" />
      </div>

      {/* Run payroll */}
      <Card>
        <CardHeader title="Run Payroll" />
        <div style={{ padding: 20, display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <label style={{ fontSize: 13, color: 'var(--muted)' }}>Month</label>
            <select value={month} onChange={e => setMonth(parseInt(e.target.value))} style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px', color: 'var(--text)', fontSize: 14, fontFamily: 'var(--font)', outline: 'none' }}>
              {Array.from({length:12},(_,i) => <option key={i+1} value={i+1}>{getMonthName(i+1)}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <label style={{ fontSize: 13, color: 'var(--muted)' }}>Year</label>
            <input type="number" value={year} onChange={e => setYear(parseInt(e.target.value))} style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px', color: 'var(--text)', fontSize: 14, fontFamily: 'var(--font)', outline: 'none', width: 100 }} />
          </div>
          <Btn onClick={runPayroll} disabled={processing}>{processing ? '⏳ Processing...' : `▶ Run Payroll — ${getMonthName(month)} ${year}`}</Btn>
          {empWithSalary < employees.length && (
            <span style={{ fontSize: 12, color: 'var(--warn)' }}>⚠️ {employees.length - empWithSalary} employees have no salary structure</span>
          )}
        </div>
      </Card>

      {/* Salary structures */}
      <Card>
        <CardHeader title="Employee Salary Structures" action={<span style={{ fontSize: 12, color: 'var(--muted)' }}>Click Set Salary to configure</span>} />
        <Table headers={['Employee', 'Code', 'Basic', 'HRA', 'Allowances', 'Gross/Mo', 'Actions']}>
          {employees.length === 0 ? <EmptyRow cols={7} /> :
           employees.map(emp => {
            const s = emp.salary_structures?.[0];
            const gross = (s?.basic_salary || 0) + (s?.hra || 0) + (s?.allowances || 0);
            return (
              <tr key={emp.id}>
                <Td><div style={{ fontWeight: 600 }}>{emp.full_name}</div><div style={{ fontSize: 11, color: 'var(--muted)' }}>{emp.email}</div></Td>
                <Td mono>{emp.employee_code || '—'}</Td>
                <Td mono>{s ? `₹${s.basic_salary?.toLocaleString('en-IN')}` : <span style={{ color: 'var(--warn)' }}>Not set</span>}</Td>
                <Td mono>{s ? `₹${s.hra?.toLocaleString('en-IN')}` : '—'}</Td>
                <Td mono>{s ? `₹${s.allowances?.toLocaleString('en-IN')}` : '—'}</Td>
                <Td mono><span style={{ color: 'var(--accent)', fontWeight: 700 }}>{gross ? `₹${gross.toLocaleString('en-IN')}` : '—'}</span></Td>
                <Td><Btn variant="outline" size="sm" onClick={() => openSalary(emp)}>Set Salary</Btn></Td>
              </tr>
            );
          })}
        </Table>
      </Card>

      {/* Payroll history */}
      <Card>
        <CardHeader title="Payroll History" />
        <Table headers={['Period', 'Employees', 'Total Amount', 'Status', 'Actions']}>
          {payrollRuns.length === 0 ? <EmptyRow cols={5} message="No payroll runs yet" /> :
           payrollRuns.map(run => (
            <tr key={run.id}>
              <Td><strong>{getMonthName(run.month)} {run.year}</strong></Td>
              <Td mono>{payrollRecords.filter(r => r.payroll_run_id === run.id).length || '—'}</Td>
              <Td mono><span style={{ color: 'var(--accent)', fontWeight: 700 }}>₹{(run.total_amount || 0).toLocaleString('en-IN')}</span></Td>
              <Td><Badge type={run.status === 'approved' ? 'green' : run.status === 'processing' ? 'yellow' : 'blue'}>{run.status}</Badge></Td>
              <Td>
                <div style={{ display: 'flex', gap: 6 }}>
                  <Btn variant="outline" size="sm" onClick={async () => { setSelectedRun(run); await loadRunRecords(run.id); }}>View</Btn>
                  {run.status === 'draft' && <Btn size="sm" onClick={() => approveRun(run.id)}>Approve</Btn>}
                </div>
              </Td>
            </tr>
          ))}
        </Table>
      </Card>

      {/* Payroll run details modal */}
      {selectedRun && (
        <Modal title={`Payroll — ${getMonthName(selectedRun.month)} ${selectedRun.year}`} onClose={() => setSelectedRun(null)} width={800}>
          <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 14, color: 'var(--muted)' }}>Total: <strong style={{ color: 'var(--accent)', fontSize: 18 }}>₹{(selectedRun.total_amount || 0).toLocaleString('en-IN')}</strong></span>
            <Badge type={selectedRun.status === 'approved' ? 'green' : 'yellow'}>{selectedRun.status}</Badge>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead><tr>{['Employee','Days','Present','Absent','Late','Gross','Deductions','Net'].map(h => <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', borderBottom: '1px solid var(--border)' }}>{h}</th>)}</tr></thead>
              <tbody>
                {payrollRecords.map(r => (
                  <tr key={r.id}>
                    <td style={{ padding: '12px', borderBottom: '1px solid var(--border)' }}><div style={{ fontWeight: 600 }}>{r.user?.full_name}</div><div style={{ fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--mono)' }}>{r.user?.employee_code}</div></td>
                    <td style={{ padding: '12px', borderBottom: '1px solid var(--border)', fontFamily: 'var(--mono)' }}>{r.working_days}</td>
                    <td style={{ padding: '12px', borderBottom: '1px solid var(--border)', fontFamily: 'var(--mono)', color: 'var(--accent)' }}>{r.present_days}</td>
                    <td style={{ padding: '12px', borderBottom: '1px solid var(--border)', fontFamily: 'var(--mono)', color: 'var(--danger)' }}>{r.absent_days}</td>
                    <td style={{ padding: '12px', borderBottom: '1px solid var(--border)', fontFamily: 'var(--mono)', color: 'var(--warn)' }}>{r.late_days}</td>
                    <td style={{ padding: '12px', borderBottom: '1px solid var(--border)', fontFamily: 'var(--mono)' }}>₹{r.gross_salary?.toLocaleString('en-IN')}</td>
                    <td style={{ padding: '12px', borderBottom: '1px solid var(--border)', fontFamily: 'var(--mono)', color: 'var(--danger)' }}>-₹{r.deductions?.toLocaleString('en-IN')}</td>
                    <td style={{ padding: '12px', borderBottom: '1px solid var(--border)', fontFamily: 'var(--mono)', color: 'var(--accent)', fontWeight: 700 }}>₹{r.net_salary?.toLocaleString('en-IN')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <Btn variant="outline" onClick={() => setSelectedRun(null)}>Close</Btn>
            {selectedRun.status === 'draft' && <Btn onClick={() => { approveRun(selectedRun.id); setSelectedRun(null); }}>✅ Approve Payroll</Btn>}
          </div>
        </Modal>
      )}

      {/* Salary modal */}
      {salaryModal && (
        <Modal title={`Salary Structure — ${salaryModal.full_name}`} onClose={() => setSalaryModal(null)}>
          <div style={{ display: 'grid', gap: 14 }}>
            <FormField label="Basic Salary (₹/month)"><Input type="number" value={salaryForm.basic_salary} onChange={e => setSalaryForm(f => ({...f, basic_salary: e.target.value}))} placeholder="20000" /></FormField>
            <FormField label="HRA (₹/month)" hint="House Rent Allowance"><Input type="number" value={salaryForm.hra} onChange={e => setSalaryForm(f => ({...f, hra: e.target.value}))} placeholder="8000" /></FormField>
            <FormField label="Other Allowances (₹/month)"><Input type="number" value={salaryForm.allowances} onChange={e => setSalaryForm(f => ({...f, allowances: e.target.value}))} placeholder="5000" /></FormField>
            {(parseFloat(salaryForm.basic_salary) + parseFloat(salaryForm.hra) + parseFloat(salaryForm.allowances)) > 0 && (
              <div style={{ background: '#00C9A710', border: '1px solid #00C9A730', borderRadius: 10, padding: 14 }}>
                <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>Monthly Breakdown</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                  <span style={{ color: 'var(--muted)' }}>Gross Salary</span>
                  <span style={{ color: 'var(--accent)', fontWeight: 700 }}>₹{(parseFloat(salaryForm.basic_salary||'0') + parseFloat(salaryForm.hra||'0') + parseFloat(salaryForm.allowances||'0')).toLocaleString('en-IN')}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginTop: 4 }}>
                  <span style={{ color: 'var(--muted)' }}>PF Deduction (12%)</span>
                  <span style={{ color: 'var(--danger)' }}>-₹{Math.round(parseFloat(salaryForm.basic_salary||'0') * 0.12).toLocaleString('en-IN')}</span>
                </div>
                <div style={{ borderTop: '1px solid var(--border)', marginTop: 8, paddingTop: 8, display: 'flex', justifyContent: 'space-between', fontSize: 14, fontWeight: 700 }}>
                  <span>Net Salary (Est.)</span>
                  <span style={{ color: 'var(--accent)' }}>₹{Math.round((parseFloat(salaryForm.basic_salary||'0') + parseFloat(salaryForm.hra||'0') + parseFloat(salaryForm.allowances||'0')) - parseFloat(salaryForm.basic_salary||'0') * 0.12).toLocaleString('en-IN')}</span>
                </div>
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
            <Btn variant="outline" onClick={() => setSalaryModal(null)}>Cancel</Btn>
            <Btn onClick={saveSalaryStructure}>Save</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}
